import type { ApiErrorBody, TokenResponse } from '../../shared/api';

/**
 * Fetch wrapper for the REST API. Access tokens live in memory only; the refresh token is an
 * httpOnly cookie, so a page reload restores the session through /api/auth/refresh.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: any,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<TokenResponse | null> | null = null;
type SessionListener = (session: TokenResponse | null) => void;
const listeners = new Set<SessionListener>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function onSessionChange(fn: SessionListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Exchanges the refresh cookie for a new access token (deduplicated across concurrent callers). */
export function refreshSession(): Promise<TokenResponse | null> {
  if (!refreshing) {
    refreshing = fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'fetch' },
    })
      .then(async (res) => {
        if (!res.ok) return null;
        return (await res.json()) as TokenResponse;
      })
      .catch(() => null)
      .then((session) => {
        accessToken = session?.accessToken ?? null;
        listeners.forEach((l) => l(session));
        return session;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  form?: FormData;
  signal?: AbortSignal;
  /** Return the raw Response (for downloads). */
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('/api') ? path : `/api${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function toError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = await res.json();
  } catch {
    /* not JSON */
  }
  const fallback =
    res.status === 413
      ? 'The file is too large'
      : res.status === 429
        ? 'Too many requests. Please wait a moment.'
        : res.status >= 500
          ? 'The server had a problem. Please try again.'
          : `Request failed (${res.status})`;
  return new ApiError(res.status, body.error ?? 'error', body.message ?? fallback, body.details, body.requestId);
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = { 'X-Requested-With': 'fetch' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), { method, headers, body, credentials: 'same-origin', signal: opts.signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.');
  }
  if (res.status === 401 && !retried && !path.includes('/auth/login') && !path.includes('/auth/refresh')) {
    const session = await refreshSession();
    if (session) return request<T>(method, path, opts, true);
  }
  if (!res.ok) throw await toError(res);
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  return (type.includes('application/json') ? res.json() : res.text()) as Promise<T>;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) => request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(method: 'POST' | 'PUT', path: string, form: FormData) => request<T>(method, path, { form }),
  /** Fetches binary content (with authentication). */
  async blob(path: string, query?: RequestOptions['query']): Promise<Blob> {
    const res = await request<Response>('GET', path, { query, raw: true });
    return res.blob();
  },
  async arrayBuffer(path: string, query?: RequestOptions['query']): Promise<ArrayBuffer> {
    const res = await request<Response>('GET', path, { query, raw: true });
    return res.arrayBuffer();
  },
};

/** Downloads an authenticated file and saves it with the server-provided name. */
export async function downloadFile(path: string, query?: RequestOptions['query']): Promise<void> {
  const res = await request<Response>('GET', path, { query: { ...query, download: 1 }, raw: true });
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition) ?? /filename="([^"]+)"/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1]) : 'download';
  saveBlob(blob, name);
}

export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}
